import { useEffect, useState } from 'react';
import { Button, Header, Icon, Input, Select, DatePicker, Checkbox } from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import { RoleComponent } from 'components/roleComponent';
import { adjustDatesToFrom, formatDate } from 'helpers';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useUser from 'helpers/hooks/useUser';
import useFetch from 'helpers/hooks/useFetch';
import { HSL, COMPANY_MANAGER, notificationAtom } from 'resources';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
    DeliveryData,
    FilterDeliveryDTO,
    GroupedDelivery,
    GuiaData,
    SelectOption,
    UpdateCarrierDelivery
} from 'models/hsl/general';
import { hslCountriesAtom, hslPartnersAtom, hslPartsAtom } from 'resources/atoms/hslAtom';
import { CompanyDTO } from 'models/userManager/addCustomer';
import { downloadFileExcelDelivery } from 'resources/api/hsl_api/fileReports';

const initialFilter: FilterDeliveryDTO = {
    IdSap: '',
    CompanyName: [],
    Country: [],
    Delivery: '',
    BillOfLading: '',
    Carrier: [],
    PartNumber: [],
    DateStart: '',
    DateEnd: '',
    PageIndex: 1,
    PageSize: 10,
    StatusGuia: null,
    StatusPartner: null
};

function DeliverySearch() {
    const navigate = useNavigate();
    const [partners, setPartners] = useRecoilState(hslPartnersAtom);
    const [countries, setCountries] = useRecoilState(hslCountriesAtom);
    const [parts, setParts] = useRecoilState(hslPartsAtom);
    const { user: userDetails } = useUser();
    const { t } = useTranslation(['common', 'hsl', 'user', 'home']);
    const setNotification = useSetRecoilState(notificationAtom);
    const [filters, setFilters] = useState<FilterDeliveryDTO>(initialFilter);
    const [filteredData, setFilteredData] = useState<GroupedDelivery[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [carrierOptions, setCarrierOptions] = useState<SelectOption[]>([]);
    const [partsOptions, setPartsOptions] = useState<SelectOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [updatingDeliveryId, setUpdatingDeliveryId] = useState<number | null>(null);

    const [, getDeliveries, loadingDeliveries] = useFetch<FilterDeliveryDTO, any>({
        url: HSL.getDeliveries,
        method: 'POST'
    });

    const [, updateStatusGuia] = useFetch<GuiaData[], any>({
        url: HSL.updateBillOfLadings,
        method: 'POST'
    });

    const [, getCarriers] = useFetch({
        url: HSL.getCarriers,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    const [, getParts] = useFetch({
        url: HSL.getParts,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    const [, getPartners] = useFetch({
        url: COMPANY_MANAGER.getHslPartners,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    const [, updateDeliveryDate, loadingUpdateDate] = useFetch<{}>({
        url: HSL.carrierDeliveryDateById,
        method: 'POST',
        onError: t('common:errors.Unexpected')
    });

    useEffect(() => {
        getFilteredDeliveries(filters);
        fetchData(getCarriers, setCarrierOptions);
        if (!partners.length) {
            getAllPartners();
        }
        if (!parts.length) {
            getAllParts();
        }
    }, []);

    useEffect(() => {
        const partsOptions = parts.map(part => ({
            value: part.PartNumber,
            text: part.PartNumber
        }));
        setPartsOptions(partsOptions);
    }, [parts]);

    const handleUpdateDeliveryDate = async (deliveryid: string, date: Date | string | null) => {
        if (!date) return;

        let formattedDate: string;

        if (date instanceof Date) {
            const year = date.getFullYear();
            const month = `${date.getMonth() + 1}`.padStart(2, '0');
            const day = `${date.getDate()}`.padStart(2, '0');
            formattedDate = `${year}-${month}-${day}`;
        } else {
            formattedDate = date;
        }

        try {
            const payload: UpdateCarrierDelivery = {
                DeliveryId: deliveryid,
                CarrierDateStr: formattedDate
            };
            await updateDeliveryDate(payload);
            getFilteredDeliveries(filters);
        } catch (error) {
            console.error('Erro ao atualizar data da transportadora:', error);
        } finally {
            setUpdatingDeliveryId(null);
        }
    };

    const fetchData = async (fetchFunction: any, setOptions: any) => {
        try {
            await fetchFunction(null, (current: any) => {
                if (current?.data) {
                    const options = current?.data?.map((item: any) => ({
                        value: item.PartNumber || item,
                        text: item.PartNumber || item
                    }));
                    setOptions(options);
                } else {
                    setNotification(old => ({ ...old, type: 'information', message: t('hsl:translation.NotFoundParts') }));
                }
            });
        } catch (error) {
            setNotification(old => ({ ...old, type: 'critical', message: t('common:errors.Unexpected') }));
        }
    };

    const getFilteredDeliveries = (filter: FilterDeliveryDTO) => {
        getDeliveries(filter, resp => {
            if (resp.data.Data) {
                setFilteredData(resp.data.Data);
                setTotalItems(resp.data.TotalCount);
            }
        });
    };

    const getAllPartners = () => {
        getPartners(null, resp => {
            if (resp.data) {
                const partnersData = resp.data.map((partner: CompanyDTO) => ({
                    value: partner.name,
                    text: partner.name
                }));
                setPartners(partnersData);

                const uniqueCountries = Array.from(new Set(resp.data.map((partner: CompanyDTO) => String(partner.country))));
                const countriesData = uniqueCountries.map(country => ({
                    value: country as string,
                    text: country as string
                }));
                setCountries(countriesData);
            }
        });
    };

    const handleExportExcel = async () => {
        await downloadFileExcelDelivery(filters, `delivery.xlsx`, handleSuccess, handleError);
    };

    const handleSuccess = () => {
        setNotification({ type: 'success', message: 'Report' });
    };
    const handleError = () => {
        setNotification({ type: 'information', message: 'NoData' });
    };

    const getAllParts = () => {
        getParts(null, (current: any) => {
            if (current.data.length) {
                setParts(current.data);
            } else {
                setNotification(old => ({ ...old, type: 'information', message: t('hsl:translation.NotFoundParts') }));
            }
        });
    };

    const applyFilters = () => {
        getFilteredDeliveries(filters);
    };

    const resetFilters = () => {
        setFilters(initialFilter);
        getFilteredDeliveries(initialFilter);
    };

    const handleChangeStatusGuia = (cellData: DeliveryData, value: boolean) => {
        const guiaData: GuiaData[] = [
            {
                Id: cellData.Id,
                IdSap: cellData.IdSap,
                GUIA: cellData.Billoflading,
                State: value ? 'OK' : 'ERRO',
                ErrorNotExist: true
            }
        ];
        updateStatusGuia(guiaData);
    };

    const headerRender = () => {
        return (
            <Header title={t('hsl:translation.NewInventory')} logo={false} menu={false}>
                <RoleComponent roles={['SUPERADMIN', 'ADMIN']}>
                    <div style={{ margin: '10px' }}>
                        <Button
                            content={t('hsl:translation.AddDelivery')}
                            type="primary"
                            size="medium"
                            iconPosition="right"
                            icon={<Icon name="file-upload" root="building" />}
                            onClick={() => navigate('new-delivery')}
                            description={t('hsl:translation.AddDelivery')}
                        />
                        <Button
                            content={t('hsl:translation.AddGuia')}
                            type="primary"
                            size="medium"
                            iconPosition="right"
                            icon={<Icon name="file-upload" root="building" />}
                            onClick={() => navigate('new-guia')}
                            description={t('hsl:translation.AddGuia')}
                        />
                    </div>
                </RoleComponent>
            </Header>
        );
    };

    const searchFieldsRender = () => {
        const isSuperAdminOrAdmin = userDetails?.role === '1' || userDetails?.role === '2';

        return (
            <div>
                <div className="form4Columns">
                    <Input
                        placeholder={t('hsl:translation.Delivery')}
                        label={t('hsl:translation.Delivery')}
                        type="text"
                        name="Delivery"
                        value={filters.Delivery}
                        onChange={value => setFilters({ ...filters, Delivery: value })}
                    />
                    <Input
                        placeholder={t('hsl:translation.BillofLading')}
                        label={t('hsl:translation.BillofLading')}
                        type="text"
                        name="Guia"
                        value={filters.BillOfLading}
                        onChange={value => setFilters({ ...filters, BillOfLading: value })}
                    />
                    <Select
                        placeholder={t('hsl:translation.Carrier')}
                        label={t('hsl:translation.Carrier')}
                        options={carrierOptions}
                        search={true}
                        value={filters.Carrier}
                        onChange={value => setFilters({ ...filters, Carrier: value })}
                        multiple={true}
                    />
                    <Select
                        placeholder={t('common:translation.PartNumber')}
                        label={t('common:translation.PartNumber')}
                        options={partsOptions}
                        search={true}
                        multiple={true}
                        value={filters.PartNumber}
                        onChange={value => setFilters({ ...filters, PartNumber: value })}
                    />
                </div>
                <div className="form4Columns">
                    <DatePicker
                        type="daterange"
                        data-id="unique-datepicker"
                        label={t('hsl:translation.RegistrationDate')}
                        rangeValue={{ to: filters?.DateEnd, from: filters?.DateStart }}
                        displayFormat={'DD-MM-YYYY'}
                        closeOnSelection={true}
                        closeOnDocumentClick={true}
                        onRangeSelect={({ to, from }) => {
                            const { DateStart, DateEnd } = adjustDatesToFrom(from, to);
                            setFilters({ ...filters, DateStart: DateStart, DateEnd: DateEnd });
                        }}
                    />
                    {!isSuperAdminOrAdmin && (
                        <>
                            <Select
                                label={t('hsl:translation.StatusBill')}
                                placeholder={t('hsl:translation.StatusPlaceholder')}
                                options={[
                                    { value: true, text: t('hsl:translation.SendOption') },
                                    { value: false, text: t('hsl:translation.ToSend') }
                                ]}
                                onChange={value => setFilters({ ...filters, StatusGuia: value })}
                                value={filters.StatusGuia}
                            />
                            <Select
                                label={t('hsl:translation.StatusPartner')}
                                placeholder={t('hsl:translation.StatusPlaceholder')}
                                options={[
                                    { value: true, text: t('hsl:translation.ReceivedOption') },
                                    { value: false, text: t('hsl:translation.ToReceivedOption') }
                                ]}
                                onChange={value => setFilters({ ...filters, StatusPartner: value })}
                                value={filters.StatusPartner}
                            />
                        </>
                    )}
                    {isSuperAdminOrAdmin && (
                        <>
                            <Select
                                label={t('user:address.Country')}
                                placeholder={t('user:address.Country')}
                                options={countries}
                                multiple={true}
                                value={filters.Country}
                                search={true}
                                onChange={value => setFilters({ ...filters, Country: value })}
                            />
                            <Select
                                label={t('common:translation.Partner')}
                                placeholder={t('common:translation.Partner')}
                                options={partners}
                                multiple={true}
                                search={true}
                                value={filters.CompanyName}
                                onChange={value => setFilters({ ...filters, CompanyName: value })}
                            />
                            <Input
                                placeholder={t('hsl:translation.IdSap')}
                                label={t('hsl:translation.IdSap')}
                                type="text"
                                name="IdSap"
                                value={filters.IdSap}
                                onChange={value => setFilters({ ...filters, IdSap: value })}
                            />
                        </>
                    )}
                </div>
                {isSuperAdminOrAdmin && (
                    <div className="form4Columns">
                        <Select
                            className="status-select"
                            label={t('hsl:translation.StatusBill')}
                            placeholder={t('hsl:translation.StatusPlaceholder')}
                            options={[
                                { value: true, text: t('hsl:translation.SendOption') },
                                { value: false, text: t('hsl:translation.ToSend') }
                            ]}
                            onChange={value => setFilters({ ...filters, StatusGuia: value })}
                            value={filters.StatusGuia}
                        />
                        <Select
                            className="status-select"
                            label={t('hsl:translation.StatusPartner')}
                            placeholder={t('hsl:translation.StatusPlaceholder')}
                            options={[
                                { value: true, text: t('hsl:translation.ReceivedOption') },
                                { value: false, text: t('hsl:translation.ToReceivedOption') }
                            ]}
                            onChange={value => setFilters({ ...filters, StatusPartner: value })}
                            value={filters.StatusPartner}
                        />
                        <div />
                        <div />
                    </div>
                )}
                <div className="buttonsSearch">
                    <Button
                        type="secondary"
                        icon="badge-delete"
                        content={t('common:actions.ResetFilters')}
                        iconPosition="right"
                        onClick={resetFilters}
                    />
                    <Button
                        type="primary"
                        icon="filter1"
                        content={t('common:actions.ApplyFilters')}
                        iconPosition="right"
                        onClick={applyFilters}
                    />
                </div>
            </div>
        );
    };

    const parseLocalDate = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-');
        return new Date(Number(year), Number(month) - 1, Number(day));
    };

    const formatDateWithoutTimezone = (date: Date) => {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatDateToBR = (date: Date) => {
        const day = `${date.getDate()}`.padStart(2, '0');
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const tableDeliveryRender = () => {
        return (
            <div className="Table-delivery">
                <DataTable columnResizeMode="fit" data={filteredData} loading={loadingDeliveries || loadingUpdateDate}>
                    <DataTable.HeaderBar>
                        <DataTable.HeaderBar.Item
                            content={t('hsl:translation.ExportExcel')}
                            iconSize="small"
                            icon="file-download"
                            onClick={handleExportExcel}
                        />
                    </DataTable.HeaderBar>
                    <DataTable.Column field="Delivery" align="center" header={t('Delivery')} sortable={true} />
                    <DataTable.Column
                        field="RegisterDate"
                        align="center"
                        header={t('hsl:translation.RegistrationDate')}
                        sortable={true}
                        renderer={e => <>{formatDate(e?.rowData.RegisterDate)}</>}
                    />
                    <DataTable.Column field="IdSap" align="center" header={t('hsl:translation.IdSap')} sortable={true} />
                    <DataTable.Column
                        field="CompanyName"
                        align="center"
                        header={t('common:translation.Partner')}
                        sortable={true}
                    />
                    <DataTable.Column field="Carrier" align="center" header={t('hsl:translation.Carrier')} sortable={true} />
                    <DataTable.Column
                        field="Billoflading"
                        align="center"
                        header={t('hsl:translation.BillofLading')}
                        sortable={true}
                    />
                    <DataTable.Column
                        field="CarrierDeliveryDate"
                        align="center"
                        header={t('hsl:translation.CarrierDeliveryDate')}
                        sortable={true}
                        renderer={e => {
                            const deliveryId = e.rowData.Id;
                            const rawDate = e.rowData.CarrierDeliveryDate;
                            const dateValue = typeof rawDate === 'string' ? parseLocalDate(rawDate) : (rawDate ?? null);

                            const isEditable = userDetails?.role === '1' || userDetails?.role === '2';
                            const isEditing = editingId === deliveryId;
                            const isUpdating = updatingDeliveryId === deliveryId;

                            if (isUpdating) {
                                return <Icon name="refresh" size="medium" loading={true} />;
                            }

                            if (!isEditable) {
                                return <span>{dateValue ? formatDateToBR(dateValue) : '-'}</span>;
                            }

                            if (dateValue && !isEditing) {
                                return (
                                    <span onClick={() => setEditingId(e.rowData.Id)} style={{ cursor: 'pointer' }}>
                                        {formatDateToBR(dateValue)}
                                    </span>
                                );
                            }

                            return (
                                <DatePicker
                                    timezone="America/Sao_Paulo"
                                    className="small-datepicker"
                                    placeholder=""
                                    loading={loadingUpdateDate}
                                    displayFormat="DD-MM-YYYY"
                                    value={dateValue ?? undefined}
                                    onChange={(date: Date | null) => {
                                        if (date instanceof Date) {
                                            const formatted = formatDateWithoutTimezone(date);
                                            handleUpdateDeliveryDate(e.rowData.Delivery, formatted);
                                            setEditingId(null);
                                        }
                                    }}
                                    onBlur={() => setEditingId(null)}
                                    autoFocus
                                    closeOnSelection
                                    closeOnDocumentClick
                                />
                            );
                        }}
                    />
                    <DataTable.Column
                        field="BillofladingApprovedDate"
                        align="center"
                        header={t('hsl:translation.BillofLadingApproveDate')}
                        sortable={true}
                        renderer={e => (
                            <>{e?.rowData.BillofladingApprovedDate ? formatDate(e?.rowData.BillofladingApprovedDate) : '-'}</>
                        )}
                    />
                    <DataTable.Column
                        field="PartnerReceivedDate"
                        align="center"
                        header={t('hsl:translation.PartnerReceivedDate')}
                        sortable={true}
                        renderer={e => <>{e?.rowData.PartnerReceivedDate ? formatDate(e?.rowData.PartnerReceivedDate) : '-'}</>}
                    />
                    {(userDetails?.role === '1' || userDetails?.role === '2') && (
                        <DataTable.Column
                            field="BillofladingApprovedDate"
                            align="center"
                            header={t('hsl:translation.StatusBill')}
                            renderer={cellData => (
                                <div>
                                    <Checkbox
                                        toggle={true}
                                        defaultChecked={cellData.value ? true : false}
                                        disabled={cellData.value ? true : false}
                                        onChange={(value: boolean) => handleChangeStatusGuia(cellData.rowData, value)}
                                    />
                                </div>
                            )}
                        />
                    )}
                    <DataTable.Column
                        field="details"
                        renderer={e => (
                            <div onClick={() => navigate('delivery-details', { state: { selectedDelivery: e?.rowData } })}>
                                <Icon size="small" root="common" name="search" className="icon-event" />
                            </div>
                        )}
                        align="center"
                        header={t('common:translation.Details')}
                    />
                </DataTable>
                <DataTable.Pagination
                    totalItems={totalItems}
                    itemsPerPage={10}
                    onPageChange={pageNumber => {
                        const updateFilters = {
                            ...filters,
                            PageIndex: typeof pageNumber === 'string' ? parseInt(pageNumber, 10) : pageNumber
                        };
                        getFilteredDeliveries(updateFilters);
                    }}
                />
            </div>
        );
    };

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <div className="container">
            {headerRender()}
            {searchFieldsRender()}
            {tableDeliveryRender()}
            <div className="button-hsl">
                <Button
                    type="secondary"
                    icon="arrow-left"
                    content={t('common:actions.Back')}
                    onClick={handleBack}
                    className="back-button"
                />
            </div>
        </div>
    );
}

export default DeliverySearch;
